/**
 * ORION — Observability Domain
 *
 * File: useObjectStream.ts
 *
 * Responsibility:
 * - Manages the SSE (Server-Sent Events) connection lifecycle.
 * - Implements hardening logic: Auto-reconnect, Exponential Backoff.
 * - Handles Auth token transmission via Query Params (when enabled).
 *
 * What this file does NOT do:
 * - Does not parse business logic of events (just passes raw JSON).
 *
 * Used by:
 * - ReplayTimeline
 */
import { useEffect, useRef, useState } from 'react';
import { getToken } from '../../utils/auth';
import type { CognitiveEvent } from './observabilityService';
import { computeBackoffDelayMs } from './backoff';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

type ConnectionStatus = 'CONNECTING' | 'OPEN' | 'CLOSED' | 'ERROR';

export function useObjectStream(
    objectId: string,
    onEvent: (event: CognitiveEvent) => void
) {
    const [status, setStatus] = useState<ConnectionStatus>('CLOSED');
    const eventSourceRef = useRef<EventSource | null>(null);
    const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const retryCountRef = useRef<number>(0);

    useEffect(() => {
        if (!objectId) return;

        connect();

        return () => {
            disconnect();
        };
    }, [objectId]);

    function connect() {
        // Don't disconnect here completely if it's a retry, but ensure clean slate
        if (eventSourceRef.current) {
            eventSourceRef.current.close();
        }

        const token = getToken();
        if (!token) {
            console.warn('[ORION] No token available for stream');
            setStatus('ERROR');
            return;
        }

        setStatus('CONNECTING');

        // If we have a token, use query param (requires ALLOW_QUERY_TOKEN_AUTH=true).
        // If we don't have a token (dev auth flow), connect without token and let DEV_AUTH handle it.
        const url = token
            ? `${API_URL}/cognition/objects/${objectId}/stream?token=${encodeURIComponent(token)}`
            : `${API_URL}/cognition/objects/${objectId}/stream`;

        const es = new EventSource(url);
        eventSourceRef.current = es;

        es.onopen = () => {
            console.log('[ORION] Stream Connected');
            setStatus('OPEN');
            retryCountRef.current = 0; // Reset backoff on success
        };

        es.onmessage = (msg) => {
            try {
                const eventData: CognitiveEvent = JSON.parse(msg.data);
                // Simple dedup check could happen here, but we rely on ID usually
                onEvent(eventData);
            } catch (e) {
                console.error('[ORION] Failed to parse event', e);
            }
        };

        es.onerror = (err: any) => {
            console.error('[ORION] Stream Error', err);

            // Explicitly close before retry logic
            es.close();
            setStatus('ERROR');

            // Exponential backoff via shared utility
            const delay = computeBackoffDelayMs(retryCountRef.current);
            console.log(`[ORION] Reconnecting in ${delay}ms... (Attempt ${retryCountRef.current + 1})`);

            retryTimeoutRef.current = setTimeout(() => {
                retryCountRef.current++;
                connect();
            }, delay);
        };
    }

    function disconnect() {
        if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
        }
        if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
            retryTimeoutRef.current = null;
        }
        setStatus('CLOSED');
    }

    return {
        status,
        reconnect: () => {
            retryCountRef.current = 0;
            if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
            connect();
        }
    };
}
